import { useState } from "react";
import { Button, Field, Input, Select, Spinner } from "@empite/ui-components";
import type {
  MarkupPlan,
  NewRoutingProfile,
  Reason,
  RoutingProfileEdit,
  RoutingProfileSummary,
} from "../admin-client";
import {
  useApplications,
  useCreateRoutingProfile,
  useMarkupPlans,
  useUpdateRoutingProfile,
} from "../admin-hooks";
import { ErrorAlert } from "./guards";
import { ReasonButton } from "./reason-prompt";
import {
  ASSUMED_EXPONENT,
  currencyExponent,
  formatUnits,
  isUnitsError,
  parseUnits,
} from "./money-units";

/**
 * The one form behind "New profile" on the routing list and "Edit profile" on
 * a profile's page.
 *
 * One component and one file rather than a copy on each screen, for the reason
 * models.tsx gives: the fields are the same fields, and two copies drift the
 * first time a column is added — the create half grows it and the edit half
 * quietly stops round tripping it, which reads to an operator as the console
 * clearing a value they never touched.
 *
 * The edit half PATCHes the WHOLE profile back, application and slug included.
 * That is the shape the API was built for: both are immutable, so echoing them
 * unchanged is a no-op and MOVING one is a 409 whose sentence names which was
 * aimed at, while a body that omitted them could not tell the two apart. It
 * also means a save that changes nothing returns the profile rather than the
 * "this edit changes nothing" 400 an empty PATCH earns.
 */

/** What the form is currently for: a new profile, or one that exists. */
export type ProfileFormTarget =
  | { mode: "create" }
  | { mode: "edit"; profile: RoutingProfileSummary };

/**
 * The currency a profile's spend ceilings are denominated in.
 *
 * The profile itself does not carry one — `routing.routing_profiles` has three
 * bigint columns of micros and no currency column — so it is read off the
 * MARKUP PLAN the profile prices against, which does carry one. A profile with
 * no plan of its own takes the platform default plan, so that is where the
 * lookup falls back to, and the answer is `undefined` when neither is
 * available (an operator without `pricing:read`, or a deployment with no
 * default plan).
 *
 * Undefined is answered honestly upstream rather than papered over with USD:
 * the exponent is what decides whether "500" is 500,000,000 micros or
 * 5,000,000,000, and a console that guessed would be off by 100x on a JPY
 * application with nothing on screen to say so.
 */
export function profileCurrency(
  plans: MarkupPlan[] | undefined,
  markupPlanID: string | undefined,
): string | undefined {
  if (!plans) {
    return undefined;
  }
  if (markupPlanID !== undefined) {
    const pinned = plans.find((plan) => plan.id === markupPlanID);
    if (pinned) {
      return pinned.currency;
    }
  }
  return plans.find((plan) => plan.is_platform_default && plan.in_force)?.currency;
}

/** The exponent to convert at, and whether it was actually known. */
export function unitScale(currency: string | undefined): { exponent: number; known: boolean } {
  const exponent = currencyExponent(currency);
  return exponent === undefined
    ? { exponent: ASSUMED_EXPONENT, known: false }
    : { exponent, known: true };
}

/** What a money box is labelled with: the currency when the page knows it,
 *  and the honest "units" when it does not. */
export function unitLabel(currency: string | undefined, known: boolean): string {
  return known && currency ? currency : "units";
}

interface ProfileFormState {
  application_id: string;
  organization_id: string;
  slug: string;
  name: string;
  markup_plan_id: string;
  max_input_tokens: string;
  max_output_tokens: string;
  per_request_limit: string;
  daily_limit: string;
  monthly_limit: string;
  fallback_enabled: boolean;
  edge_enabled: boolean;
  data_retention_days: string;
}

/** A ceiling the profile does not state is an empty box, not a zero: the
 *  column is nullable and 0006's CHECK refuses a non-positive one. */
function ceilingText(value: number | null): string {
  return value === null ? "" : String(value);
}

function limitText(value: number | null, exponent: number): string {
  return value === null ? "" : formatUnits(value, exponent, false);
}

function initialForm(target: ProfileFormTarget, exponent: number): ProfileFormState {
  if (target.mode === "create") {
    return {
      application_id: "",
      organization_id: "",
      slug: "",
      name: "",
      markup_plan_id: "",
      max_input_tokens: "",
      max_output_tokens: "",
      per_request_limit: "",
      daily_limit: "",
      monthly_limit: "",
      // 0006's defaults, shown rather than left implicit: an operator who
      // wants fallback off should have to turn it off.
      fallback_enabled: true,
      edge_enabled: true,
      data_retention_days: "30",
    };
  }
  const p = target.profile;
  return {
    application_id: p.application_id,
    organization_id: p.organization_id ?? "",
    slug: p.slug,
    name: p.name,
    markup_plan_id: p.markup_plan_id ?? "",
    max_input_tokens: ceilingText(p.max_input_tokens),
    max_output_tokens: ceilingText(p.max_output_tokens),
    per_request_limit: limitText(p.per_request_limit_micros, exponent),
    daily_limit: limitText(p.daily_limit_micros, exponent),
    monthly_limit: limitText(p.monthly_limit_micros, exponent),
    fallback_enabled: p.fallback_enabled,
    edge_enabled: p.edge_enabled,
    data_retention_days: String(p.data_retention_days),
  };
}

/** A token ceiling box, checked here only so the operator hears the rule
 *  before the request rather than after it. The server applies the same one. */
function ceilingError(value: string): string | undefined {
  const trimmed = value.trim();
  if (trimmed === "") {
    return undefined;
  }
  if (!/^\d+$/.test(trimmed)) {
    return "Whole tokens only — digits, no separators.";
  }
  return Number(trimmed) > 0 ? undefined : "Leave it empty for no limit, rather than 0.";
}

function retentionError(value: string): string | undefined {
  const trimmed = value.trim();
  if (!/^\d+$/.test(trimmed)) {
    return "Whole days. Zero means nothing is retained.";
  }
  return undefined;
}

function ceilingValue(value: string): number | null {
  const trimmed = value.trim();
  return trimmed === "" ? null : Number(trimmed);
}

/** An empty money box is "no limit of this profile's own", which the API
 *  spells `null`. A zero would be a limit of nothing, which admits nothing. */
function limitValue(value: string, exponent: number): number | null {
  const trimmed = value.trim();
  if (trimmed === "") {
    return null;
  }
  const parsed = parseUnits(trimmed, exponent);
  // Unreachable while `ready` gates the button on the same parse; if it ever
  // is reached, null is the reading that changes nothing rather than the one
  // that caps a profile at zero.
  return isUnitsError(parsed) ? null : Number(parsed.micros);
}

export function RoutingProfileForm({
  target,
  canReadPricing,
  onClose,
  onDone,
}: {
  target: ProfileFormTarget;
  /** Whether to ask for the markup plans at all — see useMarkupPlans. */
  canReadPricing: boolean;
  onClose: () => void;
  onDone: (profile: RoutingProfileSummary, message: string) => void;
}) {
  const applications = useApplications();
  const plans = useMarkupPlans({}, { enabled: canReadPricing });
  const create = useCreateRoutingProfile();
  const update = useUpdateRoutingProfile();
  const creating = target.mode === "create";

  const planRows = plans.data?.markup_plans;
  // The exponent the boxes are read at. Taken from the profile's CURRENT plan
  // rather than from whatever is selected in the form: re-pointing a profile
  // at a plan in another currency does not reinterpret the figures already in
  // the boxes, and a scale that changed under the operator mid-edit would
  // silently multiply a ceiling they had already typed.
  const currency = profileCurrency(planRows, creating ? undefined : target.profile.markup_plan_id);
  const scale = unitScale(currency);
  const [form, setForm] = useState<ProfileFormState>(() =>
    initialForm(target, unitScale(undefined).exponent),
  );
  // The edit form's money boxes are seeded before the plans have arrived, so
  // they are re-seeded once the currency is known. Keyed state (the caller
  // keys this component by target) covers switching rows; this covers the
  // first paint.
  const [seededAt, setSeededAt] = useState<number | null>(null);
  if (!creating && plans.isSuccess && seededAt !== scale.exponent) {
    setSeededAt(scale.exponent);
    setForm(initialForm(target, scale.exponent));
  }

  const set = (field: keyof ProfileFormState) => (value: string) =>
    setForm((current) => ({ ...current, [field]: value }));

  const money = {
    perRequest:
      form.per_request_limit.trim() === ""
        ? undefined
        : parseUnits(form.per_request_limit, scale.exponent),
    daily: form.daily_limit.trim() === "" ? undefined : parseUnits(form.daily_limit, scale.exponent),
    monthly:
      form.monthly_limit.trim() === "" ? undefined : parseUnits(form.monthly_limit, scale.exponent),
  };
  const errors = {
    maxInput: ceilingError(form.max_input_tokens),
    maxOutput: ceilingError(form.max_output_tokens),
    retention: retentionError(form.data_retention_days),
    perRequest:
      money.perRequest && isUnitsError(money.perRequest) ? money.perRequest.error : undefined,
    daily: money.daily && isUnitsError(money.daily) ? money.daily.error : undefined,
    monthly: money.monthly && isUnitsError(money.monthly) ? money.monthly.error : undefined,
  };
  const ready =
    form.name.trim() !== "" &&
    (!creating || (form.application_id !== "" && form.slug.trim() !== "")) &&
    Object.values(errors).every((error) => error === undefined);

  const label = creating ? "Create profile" : "Save changes";
  const name = form.name.trim() || "this profile";

  async function run(reason: Reason) {
    if (creating) {
      const body: NewRoutingProfile = {
        application_id: form.application_id,
        // Omitted rather than sent empty: a profile with no organization is
        // the application-wide default, and never mentioning one is the
        // honest description of that.
        organization_id:
          form.organization_id.trim() === "" ? undefined : form.organization_id.trim(),
        slug: form.slug.trim(),
        name: form.name.trim(),
        markup_plan_id: form.markup_plan_id === "" ? undefined : form.markup_plan_id,
        max_input_tokens: ceilingValue(form.max_input_tokens),
        max_output_tokens: ceilingValue(form.max_output_tokens),
        per_request_limit_micros: limitValue(form.per_request_limit, scale.exponent),
        daily_limit_micros: limitValue(form.daily_limit, scale.exponent),
        monthly_limit_micros: limitValue(form.monthly_limit, scale.exponent),
        fallback_enabled: form.fallback_enabled,
        edge_enabled: form.edge_enabled,
        data_retention_days: Number(form.data_retention_days.trim()),
      };
      const result = await create.mutateAsync({ body, reason });
      onDone(
        result.routing_profile,
        `${result.routing_profile.name} exists, disabled. Give it a provider chain and its model policies, then enable it.`,
      );
      return;
    }
    const body: RoutingProfileEdit = {
      // Echoed from the ROW, never from an input: both are immutable, so
      // there is no box to move them in and nothing here can ask to.
      application_id: target.profile.application_id,
      slug: target.profile.slug,
      name: form.name.trim(),
      // Sent even when empty, because empty is how a scope is WIDENED back to
      // the application default and how a plan is cleared to the platform's.
      organization_id: form.organization_id.trim(),
      markup_plan_id: form.markup_plan_id,
      max_input_tokens: ceilingValue(form.max_input_tokens),
      max_output_tokens: ceilingValue(form.max_output_tokens),
      per_request_limit_micros: limitValue(form.per_request_limit, scale.exponent),
      daily_limit_micros: limitValue(form.daily_limit, scale.exponent),
      monthly_limit_micros: limitValue(form.monthly_limit, scale.exponent),
      fallback_enabled: form.fallback_enabled,
      edge_enabled: form.edge_enabled,
      data_retention_days: Number(form.data_retention_days.trim()),
    };
    const result = await update.mutateAsync({ profileID: target.profile.id, body, reason });
    onDone(result.routing_profile, `Saved ${result.routing_profile.name}.`);
  }

  if (applications.isPending || (canReadPricing && plans.isPending)) {
    return <Spinner label="Loading the form" />;
  }

  // Superseded plan versions are not offered — a profile pins a VERSION, and
  // pinning one that has already been closed prices against a rate nobody is
  // charging — but the one this profile already points at IS offered even when
  // it is, or the Select would silently re-point the profile the moment
  // anything else was saved.
  const offered = (planRows ?? []).filter(
    (plan) => plan.in_force || plan.id === form.markup_plan_id,
  );

  return (
    <div className="esm-subform">
      <h3>{creating ? "New routing profile" : `Edit ${target.profile.name}`}</h3>
      <p className="esm-muted-text">
        {creating
          ? "A profile is born DISABLED, so it can be built beside one that is currently serving traffic and the two swapped. It routes nothing until it has a provider chain, a model policy per alias it should permit, and its status turned on."
          : "The application and the slug are fixed: the application is what fences this profile's spend off from every other one's, and the slug is the handle a runbook names it by."}
      </p>
      {/* Whatever the API said, verbatim: a 409 here is a sentence naming the
          rule that was hit — which half of the identity was aimed at, or which
          profile already holds the active slot for this scope — and a house
          failure banner would throw away the only part that says what to do
          next. */}
      <ErrorAlert
        error={create.error ?? update.error}
        title={creating ? "That profile was not created" : "That change was not saved"}
      />
      {/* Not a <form>: the write goes through the reason prompt rather than a
          submit, and an Enter key that posted straight past the prompt would
          be a mutation with no reason on it. */}
      <div className="esm-form-grid">
        <Field
          label="Application"
          htmlFor="profile-application"
          hint={creating ? "Fixed once the profile exists." : "Fixed."}
        >
          {creating ? (
            <Select
              id="profile-application"
              value={form.application_id}
              onChange={(event) => set("application_id")(event.target.value)}
            >
              <option value="">Choose an application…</option>
              {(applications.data?.applications ?? []).map((application) => (
                <option key={application.id} value={application.id}>
                  {application.name} ({application.slug})
                </option>
              ))}
            </Select>
          ) : (
            <Input id="profile-application" value={target.profile.application_name} readOnly />
          )}
        </Field>
        <Field
          label="Slug"
          htmlFor="profile-slug"
          hint={creating ? "The handle a runbook names this by. Fixed afterwards." : "Fixed."}
        >
          <Input
            id="profile-slug"
            value={form.slug}
            readOnly={!creating}
            spellCheck={false}
            onChange={(event) => set("slug")(event.target.value)}
          />
        </Field>
        <Field label="Name" htmlFor="profile-name" hint="What operators read.">
          <Input
            id="profile-name"
            value={form.name}
            onChange={(event) => set("name")(event.target.value)}
          />
        </Field>
        <Field
          label="Organization"
          htmlFor="profile-organization"
          hint="A uuid scopes this profile to one customer. Empty is the application-wide default — a different profile, not a missing value."
        >
          <Input
            id="profile-organization"
            value={form.organization_id}
            spellCheck={false}
            onChange={(event) => set("organization_id")(event.target.value)}
          />
        </Field>
        <Field
          label="Markup plan"
          htmlFor="profile-markup-plan"
          hint={
            canReadPricing
              ? "Empty takes the platform default plan."
              : "Your roles do not grant pricing:read, so the plans cannot be listed here."
          }
        >
          <Select
            id="profile-markup-plan"
            value={form.markup_plan_id}
            disabled={!canReadPricing}
            onChange={(event) => set("markup_plan_id")(event.target.value)}
          >
            <option value="">the platform default plan</option>
            {offered.map((plan) => (
              <option key={plan.id} value={plan.id}>
                {plan.name} ({plan.currency}
                {plan.in_force ? "" : ", superseded"})
              </option>
            ))}
          </Select>
        </Field>
        <Field
          label="Max input tokens"
          htmlFor="profile-max-input"
          hint="Empty is no limit of this profile's own."
          error={errors.maxInput}
        >
          <Input
            id="profile-max-input"
            inputMode="numeric"
            value={form.max_input_tokens}
            onChange={(event) => set("max_input_tokens")(event.target.value)}
          />
        </Field>
        <Field
          label="Max output tokens"
          htmlFor="profile-max-output"
          hint="Empty is no limit of this profile's own."
          error={errors.maxOutput}
        >
          <Input
            id="profile-max-output"
            inputMode="numeric"
            value={form.max_output_tokens}
            onChange={(event) => set("max_output_tokens")(event.target.value)}
          />
        </Field>
        <MoneyField
          id="profile-per-request"
          label="Per request limit"
          currency={currency}
          scale={scale}
          value={form.per_request_limit}
          parsed={money.perRequest}
          error={errors.perRequest}
          onChange={set("per_request_limit")}
        />
        <MoneyField
          id="profile-daily"
          label="Daily limit"
          currency={currency}
          scale={scale}
          value={form.daily_limit}
          parsed={money.daily}
          error={errors.daily}
          onChange={set("daily_limit")}
        />
        <MoneyField
          id="profile-monthly"
          label="Monthly limit"
          currency={currency}
          scale={scale}
          value={form.monthly_limit}
          parsed={money.monthly}
          error={errors.monthly}
          onChange={set("monthly_limit")}
        />
        <Field
          label="Fallback"
          htmlFor="profile-fallback"
          hint="Whether a failed attempt may be retried down the provider chain."
        >
          <Select
            id="profile-fallback"
            value={form.fallback_enabled ? "on" : "off"}
            onChange={(event) =>
              setForm((current) => ({ ...current, fallback_enabled: event.target.value === "on" }))
            }
          >
            <option value="on">on</option>
            <option value="off">off</option>
          </Select>
        </Field>
        <Field
          label="Edge"
          htmlFor="profile-edge"
          hint="Whether the edge worker may serve this profile's traffic."
        >
          <Select
            id="profile-edge"
            value={form.edge_enabled ? "on" : "off"}
            onChange={(event) =>
              setForm((current) => ({ ...current, edge_enabled: event.target.value === "on" }))
            }
          >
            <option value="on">on</option>
            <option value="off">off</option>
          </Select>
        </Field>
        <Field
          label="Data retention (days)"
          htmlFor="profile-retention"
          hint="Zero is keep nothing, which is not the same as keep forever."
          error={errors.retention}
        >
          <Input
            id="profile-retention"
            inputMode="numeric"
            value={form.data_retention_days}
            onChange={(event) => set("data_retention_days")(event.target.value)}
          />
        </Field>
      </div>
      <div className="esm-actions">
        <ReasonButton
          variant="primary"
          busy={create.isPending || update.isPending}
          disabled={!ready}
          mutation={{
            title: creating ? `Create ${name}` : `Save changes to ${name}`,
            tone: "primary",
            description: creating ? (
              <p>
                A disabled profile for this application. It changes no traffic on its own: nothing
                is routed through a profile until it is enabled, and nothing resolves inside it
                until it has both a provider chain and a model policy.
              </p>
            ) : (
              <p>
                {target.profile.status === "active"
                  ? "This profile is serving traffic right now, so every change here takes effect on the next request."
                  : "This profile is disabled, so nothing changes until it is enabled."}{" "}
                Limits left empty are cleared, which means no limit of this profile's own.
              </p>
            ),
            confirmLabel: label,
            run,
          }}
        >
          {label}
        </ReasonButton>
        <Button variant="ghost" disabled={create.isPending || update.isPending} onClick={onClose}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

/**
 * A spend ceiling, typed in currency units and echoed back as the exact
 * integer of micros the request will carry.
 *
 * The echo is the point. The column has no currency of its own, so the scale
 * is inferred from the markup plan and the inference can be wrong — an
 * operator can see "5.00 USD = 500,000,000 micros" and check it against the
 * figure in the ticket, which they could not do if the conversion happened
 * silently between the keyboard and the wire.
 */
function MoneyField({
  id,
  label,
  currency,
  scale,
  value,
  parsed,
  error,
  onChange,
}: {
  id: string;
  label: string;
  currency: string | undefined;
  scale: { exponent: number; known: boolean };
  value: string;
  parsed: ReturnType<typeof parseUnits> | undefined;
  error: string | undefined;
  onChange: (value: string) => void;
}) {
  const unit = unitLabel(currency, scale.known);
  const hint =
    parsed && !isUnitsError(parsed)
      ? `= ${parsed.micros.toLocaleString()} micros`
      : scale.known
        ? `In ${unit}. Empty is no limit of this profile's own.`
        : "In whole currency units at the platform's usual two decimal places — no markup plan named a currency for this profile. Empty is no limit.";

  return (
    <Field label={`${label} (${unit})`} htmlFor={id} hint={hint} error={error}>
      <Input
        id={id}
        inputMode="decimal"
        value={value}
        onChange={(event) => onChange(event.target.value)}
      />
    </Field>
  );
}
